<template>
  <v-container fluid>

    <!-- Title -->
    <Title title="Lista de usuarios"
           :description="'Registro de los' + (totalItems ? ' ' + totalItems+ ' ' : ' ') + 'usuarios en el sistema'"></Title>

    <!-- Search bar -->
    <v-row>
      <v-col cols="12" md="5">
        <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Buscar"
            outlined
            dense
            clearable
            hide-details
            class="white"
        >
        </v-text-field>
      </v-col>

      <!-- Filter by role -->
      <v-col cols="12" md="2">
        <v-select
            v-model="roleFilter"
            :items="roles"
            label="Filtrar por rol"
            outlined
            dense
            hide-details
            class="white"
        >
        </v-select>
      </v-col>

      <!-- Filter by status -->
      <v-col cols="12" md="2">
        <v-select
            v-model="statusFilter"
            :items="[
              { text: 'Habilitado', value: true },
              { text: 'Deshabilitado', value: false }
            ]"
            label="Filtrar por estado"
            outlined
            dense
            hide-details
            class="white"
        >
        </v-select>
      </v-col>

      <!-- Create button -->
      <v-col cols="12" md="3" class="d-flex align-center">
        <v-btn block color="primary" height="40" @click="openDialogCreateUser">
          <v-icon class="me-2">mdi-plus-box</v-icon>
          Registrar usuario
        </v-btn>

        <!-- Create user modal -->
        <DialogCreateUser ref="modalCreateUser" @created="getUsersPage"/>
      </v-col>
    </v-row>

    <!-- Users table -->
    <v-row>
      <v-col cols="12">
        <v-data-table
            class="border-px"
            :headers="headers"
            :items="users"
            :search="search"
            :loading="isLoading"
            :items-per-page="size"
            no-data-text="No se encontraron usuarios"
            no-results-text="No se encontraron usuarios"
            loading-text="Cargando usuarios..."
            :server-items-length="totalItems"
            @update:page="page = $event - 1"
            @update:items-per-page="size = $event"
            @click:row="handleItemClick($event)"
            :disable-filtering="true"
            :disable-sort="true"
            :footer-props="{
            itemsPerPageOptions: [5, 10, 15, 20],
            showFirstLastPage: true,
            itemsPerPageText: 'Usuarios por página',
            itemsPerPageAllText: 'Todos',
            pageText: `Página ${page + 1 } de ${totalPages}`,
            rowsPerPageText: 'Usuarios por página',
          }"
        >
          <template v-slot:item.name="{ item }">
            <span>{{ item.name }} {{ item.lastname1 }} {{ item.lastname2 }}</span>
          </template>
          <template v-slot:item.officeName="{ item }">
            <span v-if="item.officeName">
              <span class="font-weight-medium deep-orange--text">DIETEC</span>
              {{ item.officeName }}
            </span>
            <span v-else>
              Sin consultorio
            </span>
          </template>
          <template v-slot:item.role="{ item }">
            <span>{{ textRole(item.role) }}</span>
          </template>
          <template v-slot:item.status="{ item }">
            <StatusChip :status="item.status" />
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import Vue from "vue";
import Title from "@/components/Title.vue";
import UserService from "@/services/users/UserService";
import {PageUser} from "@/services/users/entities/PageUser";
import {RequestUserPage} from "@/services/users/entities/RequestUserPage";
import DialogCreateUser from "@/views/users/components/DialogCreateUser.vue";
import StatusChip from "@/components/StatusChip.vue";
import {isAllowed} from "@/utils/UniversalFunctions";
import {PERMISSIONS} from "@/utils/Permissions";

export default Vue.extend({
  name: "UsersPage",
  components: {
    StatusChip,
    Title,
    DialogCreateUser,
  },
  data() {
    return {
      // Search
      search: "" as string,
      roleFilter: "DIETICIAN" as string,
      statusFilter: true as boolean,
      page: 0 as number,
      size: 10 as number,

      // Loading
      isLoading: false as boolean,

      // Table
      users: [] as PageUser[],
      totalItems: 0 as number,
      totalPages: 0 as number,
      headers: [
        {text: "Nombre", value: "name", sortable: false},
        {text: "Consultorio", value: "officeName", sortable: false},
        {text: "Nombre de usuario", value: "username", sortable: false},
        {text: "Rol", value: "role", sortable: false},
        {text: "Estado", value: "status", sortable: false},
      ],
    };
  },

  computed: {
    roles(): Array<{ text: string; value: string }> {
      let roles = [
        { text: "Dietista", value: "DIETICIAN" },
        { text: "Administrador", value: "ADMIN" },
      ];
      if (this.$store.state.auth.user.role === "ADMIN") {
        if (!isAllowed(PERMISSIONS.DIETICIAN_MANAGE)) roles = roles.filter(role => role.value !== "DIETICIAN");
        if (!isAllowed(PERMISSIONS.ADMIN_MANAGE)) roles = roles.filter(role => role.value !== "ADMIN");
      }
      return roles;
    }
  },

  methods: {
    async getUsersPage() {
      this.isLoading = true;
      try {
        const body: RequestUserPage = {
          page: this.page,
          size: this.size,
          searchQuery: this.search,
          role: this.roleFilter,
          status: this.statusFilter,
        };
        const response = await UserService.getUserPage(body);
        this.users = response.content;
        this.totalItems = response.totalElements;
        this.totalPages = response.totalPages;
      } finally {
        this.isLoading = false;
      }
    },

    handleItemClick(item: PageUser) {
      this.$router.push(`/usuarios/${item.username}`);
    },

    textRole(role: string) {
      switch (role) {
        case "ADMIN":
          return "Administrador";
        case "DIETICIAN":
          return "Dietista";
        default:
          return "Desconocido";
      }
    },

    textStatus(status: boolean) {
      return status ? "Habilitado" : "Deshabilitado";
    },

    colorStatus(status: boolean) {
      return status ? "success" : "secondary lighten-3";
    },

    openDialogCreateUser() {
      (this.$refs.modalCreateUser as any).dialog = true;
    },
  },

  watch: {
    search: {
      handler: function (value) {
        this.page = 0;
        this.getUsersPage();
      },
      immediate: false,
    },
    page: {
      handler: function (value) {
        this.getUsersPage();
      },
      immediate: false,
    },
    size: {
      handler: function (value) {
        this.page = 0;
        this.getUsersPage();
      },
      immediate: false,
    },
    roleFilter: {
      handler: function (value) {
        this.page = 0;
        this.getUsersPage();
      },
      immediate: false,
    },
    statusFilter: {
      handler: function (value) {
        this.page = 0;
        this.getUsersPage();
      },
      immediate: false,
    },
  },

  created() {
    this.getUsersPage();
  }
});
</script>

<style scoped>
.border-px {
  border: 1px solid #e0e0e0;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}
</style>
